import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "flowbite-react";
import dayjs from "dayjs";
import { Table } from "../../../components/Table";
import CountryDisplay from "../../../components/CountryDisplay";
import DeviceDisplay from "../../../components/DeviceLabel";
import { useAppOrdersDetail } from "../../../hooks";
import { IOrder } from "../interface";
import { formatNumber } from "../../../lib/utils";
import { CurrencyCode } from "../../../shared/constants";
import { getCurrencySymbol } from "../../../lib/utils";
import { useDateRange } from "../../../contexts/DateRangeContext";

interface AppOrdersSubTableProps {
  appSlug: string;
  onOrderClick: (order: IOrder) => void;
  social_account_id?: number;
  country?: string;
  status?: string;
  search?: string;
  loadingRowCount?: number;
  orders?: IOrder[];
}

const AppOrdersSubTable: React.FC<AppOrdersSubTableProps> = ({
  appSlug,
  onOrderClick,
  social_account_id,
  country,
  status,
  search,
  loadingRowCount = 3,
  orders
}) => {
  const { t } = useTranslation();
  const { dateRange } = useDateRange();
  const { data: ordersData, isLoading } = useAppOrdersDetail({
    slug: appSlug,
    startDate: dateRange.startDate || undefined,
    endDate: dateRange.endDate || undefined,
    page: 1,
    per_page: 100,
    social_account_id,
    country,
    status,
    search,
    enabled: !orders,
  });

  const displayOrders = useMemo(() => orders || ordersData?.results || [], [orders, ordersData]);
  const displayLoading = !orders && isLoading;

  const columns: ColumnDef<IOrder>[] = useMemo(() => [
    {
      id: "order_id",
      accessorKey: "order_id",
      header: t("orders.columns.orderId"),
      size: 180,
      cell: ({ row }) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOrderClick(row.original);
          }}
          className="text-primary hover:underline font-semibold text-sm"
        >
          {row.original.order_id}
        </button>
      ),
    },
    {
      id: "transaction_datetime",
      accessorKey: "transaction_date",
      header: t("orders.columns.transactionDate"),
      size: 160,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-gray-800">
            {dayjs(row.original.transaction_date).format("MMM DD, YYYY")}
          </span>
          <span className="text-[10px] text-gray-500 font-medium uppercase">
            {row.original.transaction_time}
          </span>
        </div>
      ),
    },
    {
      id: "buyer_info",
      accessorKey: "buyer_country",
      header: t("orders.columns.buyerCountry"),
      size: 180,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <CountryDisplay
            countryCode={row.original.buyer_country}
            countryName={row.original.buyer_country}
            fontSize="sm"
            countryNameClasses="text-gray-800 font-bold"
          />
          <DeviceDisplay
            device={row.original.device_model}
            className="text-xs text-gray-500 font-medium"
            showIcon={true}
          />
        </div>
      ),
    },
    {
      id: "amount",
      accessorKey: "amount_paid_buyer_currency",
      header: t("orders.columns.totalGross"),
      size: 140,
      cell: ({ row }) => {
        const symbol = getCurrencySymbol(row.original.buyer_currency as CurrencyCode);
        return (
          <div className="flex flex-col gap-0.5">
            <span className={`text-sm font-bold ${row.original.amount_paid_buyer_currency < 0 ? "text-red-500" : "text-gray-800"}`}>
              {symbol}{formatNumber(row.original.amount_paid_buyer_currency)} {row.original.buyer_currency}
            </span>
            <span className="text-[10px] text-yellow-500 font-medium whitespace-nowrap">
              Tax: {symbol}{formatNumber(row.original.amount_tax || 0)}
            </span>
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "transaction_type",
      header: t("orders.detail.status").toUpperCase(),
      size: 100,
      cell: ({ row }) => {
        const status = row.original.transaction_type;
        const color = status?.toLowerCase() === "charged" ? "success" : "warning";
        return (
          <Badge color={color} size="xs" className="font-bold rounded w-fit uppercase">
            {status || "PENDING"}
          </Badge>
        );
      },
    },
  ], [t, onOrderClick]);

  return (
    <div className="p-2 bg-gray-50/50 border-t border-gray-100">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <Table
          data={displayOrders}
          columns={columns}
          isLoading={displayLoading}
          loadingRowCount={loadingRowCount}
          showPagination={false}
          pageSize={100}
          showSearch={false}
          maxHeight="none"
          onRowClick={(order) => onOrderClick(order)}
        />
      </div>
    </div>
  );
};

export default AppOrdersSubTable;
